import { useState, type FormEvent } from 'react';
import { createRoute } from '@tanstack/react-router';
import { IconSparkles, IconInfoCircle } from '@tabler/icons-react';
import {
  COURSE_CATEGORIES,
  DEFAULT_CATEGORY_LABELS,
  AQF_LEVELS,
  AQF_LEVEL_LABELS,
  AU_STATES,
  INTAKE_MONTHS,
  IELTS_MAX_OPTIONS,
  type CourseCategory,
  type AqfLevel,
} from '@admitly/shared';
import { Route as appRoute } from '../app.js';
import { Button } from '../../components/ui/Button.js';
import { Input } from '../../components/ui/Input.js';
import { Label } from '../../components/ui/Label.js';
import { Select } from '../../components/ui/Select.js';
import { Textarea } from '../../components/ui/Textarea.js';
import { CourseCard } from '../../components/courses/CourseCard.js';
import { useShortlistIds, useToggleShortlist } from '../../hooks/useShortlist.js';
import { useSuggestions, type SuggestRequest } from '../../hooks/useSuggestions.js';

export const Route = createRoute({
  getParentRoute: () => appRoute,
  // Auth is enforced by the /app parent's beforeLoad.
  path: '/suggest',
  component: SuggestPage,
});

function SuggestPage() {
  const [field, setField] = useState('');
  const [category, setCategory] = useState('');
  const [aqf, setAqf] = useState('');
  const [state, setState] = useState('');
  const [budget, setBudget] = useState('');
  const [ielts, setIelts] = useState('');
  const [intake, setIntake] = useState('');
  const [notes, setNotes] = useState('');

  const suggest = useSuggestions();
  const shortlistIds = useShortlistIds();
  const toggleShortlist = useToggleShortlist();
  const result = suggest.data;

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    const req: SuggestRequest = {};
    if (field.trim()) req.field = field.trim();
    if (category) req.category = category;
    if (aqf) req.aqf = aqf;
    if (state) req.state = state;
    if (budget && Number(budget) > 0) req.budgetMaxAud = Number(budget);
    if (ielts) req.ieltsMax = Number(ielts);
    if (intake) req.intakeMonth = Number(intake);
    if (notes.trim()) req.notes = notes.trim();
    suggest.mutate(req);
  }

  return (
    <div className="max-w-6xl">
      <div className="mb-2 text-xs uppercase tracking-[0.14em] text-muted font-semibold">Catalogue</div>
      <h1 className="font-serif text-4xl tracking-tighter2 mb-2">Suggest courses</h1>
      <p className="text-muted text-sm mb-6 max-w-2xl">
        Describe the student's course requirements and we'll rank the best matches from your catalogue.
      </p>

      {/* Privacy note */}
      <div className="surface-soft flex items-start gap-2 px-4 py-3 mb-6 text-sm text-muted max-w-2xl">
        <IconInfoCircle size={18} className="shrink-0 text-navy mt-0.5" />
        <span>
          Enter <strong className="text-ink-soft">course requirements only</strong> — please don't
          include the student's name, email, or any identifying details.
        </span>
      </div>

      <form onSubmit={handleSubmit} className="surface p-5 sm:p-6 mb-8 max-w-3xl">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="sm:col-span-2">
            <Label htmlFor="field">Field / area of study</Label>
            <Input
              id="field"
              value={field}
              onChange={(e) => setField(e.target.value)}
              placeholder="e.g. nursing, data science, business"
            />
          </div>

          <div>
            <Label htmlFor="category">Category</Label>
            <Select id="category" value={category} onChange={(e) => setCategory(e.target.value)}>
              <option value="">Any</option>
              {COURSE_CATEGORIES.map((c: CourseCategory) => (
                <option key={c} value={c}>
                  {DEFAULT_CATEGORY_LABELS[c]}
                </option>
              ))}
            </Select>
          </div>

          <div>
            <Label htmlFor="aqf">Level</Label>
            <Select id="aqf" value={aqf} onChange={(e) => setAqf(e.target.value)}>
              <option value="">Any</option>
              {AQF_LEVELS.map((l: AqfLevel) => (
                <option key={l} value={l}>
                  {AQF_LEVEL_LABELS[l]}
                </option>
              ))}
            </Select>
          </div>

          <div>
            <Label htmlFor="state">Preferred state</Label>
            <Select id="state" value={state} onChange={(e) => setState(e.target.value)}>
              <option value="">Any</option>
              {AU_STATES.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </Select>
          </div>

          <div>
            <Label htmlFor="budget">Budget — max annual tuition (AUD)</Label>
            <Input
              id="budget"
              type="number"
              min={0}
              value={budget}
              onChange={(e) => setBudget(e.target.value)}
              placeholder="e.g. 35000"
            />
          </div>

          <div>
            <Label htmlFor="ielts">English — max IELTS required</Label>
            <Select id="ielts" value={ielts} onChange={(e) => setIelts(e.target.value)}>
              <option value="">Any</option>
              {IELTS_MAX_OPTIONS.map((b) => (
                <option key={b} value={b}>
                  ≤ {b.toFixed(1)}
                </option>
              ))}
            </Select>
          </div>

          <div>
            <Label htmlFor="intake">Intake month</Label>
            <Select id="intake" value={intake} onChange={(e) => setIntake(e.target.value)}>
              <option value="">Any</option>
              {INTAKE_MONTHS.map((m) => (
                <option key={m.value} value={m.value}>
                  {m.label}
                </option>
              ))}
            </Select>
          </div>

          <div className="sm:col-span-2">
            <Label htmlFor="notes">Anything else (optional)</Label>
            <Textarea
              id="notes"
              rows={3}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="e.g. wants a co-op placement, prefers a coastal campus, PR pathway important"
            />
          </div>
        </div>

        <div className="mt-5">
          <Button type="submit" loading={suggest.isPending} leftIcon={<IconSparkles size={16} />}>
            Suggest courses
          </Button>
        </div>
      </form>

      {suggest.isError && (
        <div className="surface p-5 text-danger text-sm mb-6">
          Couldn't get suggestions: {(suggest.error as Error).message}
        </div>
      )}

      {result && (
        <>
          {result.notice && (
            <div className="rounded-lg bg-warn-bg border border-warn/30 px-4 py-3 text-sm text-warn mb-6 max-w-3xl">
              {result.notice}
            </div>
          )}

          {result.items.length === 0 ? (
            <div className="surface-soft p-8 text-center">
              <h3 className="font-serif text-lg text-ink mb-1">No matching courses</h3>
              <p className="text-sm text-muted max-w-md mx-auto">
                Try relaxing the budget, level, or location filters.
              </p>
            </div>
          ) : (
            <>
              <p className="text-muted text-sm mb-3">
                {result.aiUsed ? 'Ranked by best fit' : 'Matching your filters'} ·{' '}
                {result.items.length} course{result.items.length === 1 ? '' : 's'}
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {result.items.map(({ course, reason }) => {
                  const saved = shortlistIds.has(course.id);
                  return (
                    <div key={course.id} className="flex flex-col gap-2">
                      {reason && (
                        <div className="rounded-lg border-l-2 border-gold bg-gold-soft/15 px-3 py-2 text-xs text-ink-soft">
                          <span className="font-semibold text-navy">Why it fits: </span>
                          {reason}
                        </div>
                      )}
                      <CourseCard
                        course={course}
                        isStarred={saved}
                        onToggleStar={(courseId) => toggleShortlist.mutate({ courseId, saved })}
                      />
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}
